import { MAX_QUERY_LENGTH, parse, serialize } from './kql/kql';

/** Is this text legal KQL? The grammar is the only judge worth asking. */
const canParse = (text) => {
	try {
		parse(text);
		return true;
	} catch (e) {
		return false;
	}
};

/**
 * The KQL the item feed actually runs while a worklist is on screen.
 *
 * A card's `options.worklist.filter` says which of its items a pass is MEANT
 * to work through — "only pre-trip inspections", "only the residents on this
 * hall". That narrowing runs server-side, composed into the same `?q=` the
 * feed already sends, because the feed is paged (skip/limit plus
 * x-total-count) and a client-side .filter() would narrow only the page in
 * hand while the total, the infinite-scroll sentinel and the footer count
 * kept speaking for the whole unfiltered set.
 *
 * The worklist filter NARROWS what the reader is already looking at rather
 * than replacing it — that is what the option's own help text promises — so
 * the two sides are ANDed with each parenthesised, because either may be an
 * `or` and neither may swallow the other.
 *
 * THE STORED FILTER IS PARSED BEFORE IT IS SENT, and that is not a nicety.
 * `useCollectionItems` answers a 400 on `q` by calling onQueryError, which on
 * the items page clears the USER's query — so a card whose stored filter is
 * unreadable would silently delete the filter its reader typed. An
 * unreadable stored filter is therefore dropped here.
 *
 * DROPPING IT IS NOT FREE, and this returns `{ query, dropped }` rather than
 * a bare string so that it cannot be free. A worklist whose filter went
 * missing lists every item of the card as the pass — including the rows the
 * filter was written to exclude — and the reader marks and SAVES those rows,
 * writing records and firing rules outside the round. The caller is told why
 * the narrowing is gone so it can refuse to draw a pass it cannot honour.
 *
 * `dropped` is one of:
 *   null          — `query` is the composition (or there was nothing to compose)
 *   'unreadable'  — the stored filter is not legal KQL
 *   'too-long'    — the composition exceeds what the query engine accepts
 *
 * The reader's OWN query is passed through untouched even when it will not
 * parse (a stale bookmark, a link from an older query engine), and that is
 * NOT reported as a drop: the server refuses their text, the existing 400
 * path says so and clears it, and the next composition — with the bad text
 * gone — carries the stored filter after all. Blaming the card for the
 * reader's typo would be a lie with a round trip on it.
 */
export function composeWorklistQuery({ userQuery = '', worklistFilter = '' } = {}) {
	const own = String(userQuery || '').trim();
	const filter = String(worklistFilter || '').trim();

	if (!filter) return { query: own, dropped: null };
	// The reader's query is theirs to be wrong about — hand it back as typed
	// and let the server's 400 path have it.
	if (own && !canParse(own)) return { query: own, dropped: null };
	if (!canParse(filter)) return { query: own, dropped: 'unreadable' };

	const text = own ? `(${filter}) and (${own})` : filter;
	// The cap is the query engine's (utils/kql), enforced identically on both
	// sides of the wire. Two filters that each fit can compose into one that
	// does not, and a pass that quietly loses its narrowing at 2000 characters
	// is the exact failure this function exists to make impossible.
	if (text.length > MAX_QUERY_LENGTH) return { query: own, dropped: 'too-long' };

	try {
		const composed = serialize(parse(text));
		if (composed.length > MAX_QUERY_LENGTH) return { query: own, dropped: 'too-long' };
		return { query: composed, dropped: null };
	} catch (e) {
		// Unreachable with two sides that each parse and are each
		// parenthesised, but a composer that can throw is a composer that can
		// take the whole list down with it.
		return { query: own, dropped: 'unreadable' };
	}
}

export default composeWorklistQuery;
